import {
  Alert,
  Button,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useState } from 'react'
import {
  useDiscountRates,
  useRemoveDiscountRate,
  useSaveDiscountRate,
  type DiscountRate,
} from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { EmptyState, ErrorBanner } from '@/components/common'

/**
 * The discounts the business gives.
 *
 * <p>A discount was a number typed into a box, so the same job was given three
 * per cent at one desk and ten at another and what the business allows was
 * written down nowhere. This is the list it gives, offered wherever a discount
 * is entered.</p>
 *
 * <p>It is not the authority. What a desk may agree without asking is the
 * approval matrix, and a figure beyond it still goes to a manager - whether it
 * came off this list or was typed.</p>
 */
export function DiscountsTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const writable = canWrite('config')
  const { data: rates, error } = useDiscountRates({ includeInactive: true })
  const save = useSaveDiscountRate()
  const remove = useRemoveDiscountRate()

  const [percent, setPercent] = useState('')
  const [label, setLabel] = useState('')

  const add = () => {
    if (!percent) return
    save.mutate(
      { body: { percent: Number(percent), label: label.trim() || undefined, sortOrder: 99 } },
      {
        onSuccess: () => {
          setPercent('')
          setLabel('')
        },
      },
    )
  }

  /** Corrections are saved as they are made: one figure, one field, no form. */
  const edit = (rate: DiscountRate, patch: Partial<DiscountRate>) =>
    save.mutate({
      id: rate.id,
      body: {
        percent: patch.percent ?? rate.percent,
        label: (patch.label ?? rate.label) || undefined,
        sortOrder: patch.sortOrder ?? rate.sortOrder,
        active: patch.active ?? rate.active,
      },
    })

  return (
    <>
      <Typography variant="caption" color="text.secondary">
        Offered wherever a discount is entered, so the usual answers are one click. A figure can
        still be typed — this is the list, not the authority. What a desk may agree without asking
        is the approvals matrix.
      </Typography>

      <ErrorBanner error={error ?? save.error ?? remove.error} />

      {writable ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Discount %"
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') add()
            }}
            inputProps={{ min: 0.5, max: 100, step: 0.5 }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="What it is given for"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Repeat customer, trade, staff"
            helperText="Optional"
            sx={{ flexGrow: 1, maxWidth: 320 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!percent || save.isPending}
            onClick={add}
          >
            Add
          </Button>
        </Stack>
      ) : null}

      {(rates ?? []).length === 0 ? (
        <EmptyState message="No discount is on the list yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={140}>Discount</TableCell>
              <TableCell>What it is given for</TableCell>
              <TableCell width={110} align="center">
                Offered
              </TableCell>
              <TableCell width={60} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(rates ?? []).map((rate) => (
              <TableRow key={rate.id} hover>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    defaultValue={rate.percent}
                    disabled={!writable}
                    inputProps={{ min: 0.5, max: 100, step: 0.5, 'aria-label': 'Discount percent' }}
                    onBlur={(event) => {
                      const next = Number(event.target.value)
                      if (next && next !== rate.percent) edit(rate, { percent: next })
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    defaultValue={rate.label ?? ''}
                    disabled={!writable}
                    placeholder="—"
                    inputProps={{ 'aria-label': `What ${rate.percent}% is given for` }}
                    onBlur={(event) => {
                      if (event.target.value !== (rate.label ?? '')) {
                        edit(rate, { label: event.target.value })
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  {/* Withdrawn rather than deleted: a job sold at seven stays
                      sold at seven, and the list has to be able to say what was
                      offered that month. */}
                  <Switch
                    size="small"
                    checked={rate.active}
                    disabled={!writable}
                    onChange={(event) => edit(rate, { active: event.target.checked })}
                    inputProps={{ 'aria-label': `Offer ${rate.percent}%` }}
                  />
                </TableCell>
                <TableCell align="right">
                  {writable && rate.active ? (
                    <Tooltip title="Take it off the list">
                      <IconButton
                        size="small"
                        aria-label={`Remove ${rate.percent}%`}
                        onClick={() => remove.mutate(rate.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {(rates ?? []).some((rate) => !rate.active) ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          A discount switched off is no longer offered. Jobs already sold at it keep it.
        </Alert>
      ) : null}
    </>
  )
}
